/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { store } from '..'
import { noteTreeActionsCreator } from './slice'
import { getNoteTree } from '../../api/explore'
import { Logger } from '../../utils/logger'
import type { NoteTreeEntryOwnerUpdate, NoteTreeEntryRename, NoteTreeEntryUpdate } from './types'

const logger = new Logger('NoteTreeMethods')

const SIDEBAR_OPEN_LOCAL_STORAGE_KEY = 'noteTreeSidebarOpen'

/**
 * Loads all notes the user can read from the backend into the note tree.
 * Failures are only logged, because the tree is refreshed periodically anyway.
 */
export const loadNoteTree = async (): Promise<void> => {
  try {
    const entries = await getNoteTree()
    store.dispatch(noteTreeActionsCreator.setNoteTreeEntries(entries))
  } catch (error) {
    logger.error('Failed to load note tree', error)
  }
}

/**
 * Updates the title, path and badge of a note in the tree without waiting for the backend,
 * e.g. while the note is being edited.
 *
 * @param update The new title, path and badge of the note
 */
export const updateNoteTreeEntry = (update: NoteTreeEntryUpdate): void => {
  store.dispatch(noteTreeActionsCreator.updateNoteTreeEntry(update))
}

/**
 * Updates the owner of a note in the tree, e.g. after the ownership was transferred.
 *
 * @param update The primary alias of the note and its new owner
 */
export const setNoteTreeEntryOwner = (update: NoteTreeEntryOwnerUpdate): void => {
  store.dispatch(noteTreeActionsCreator.setNoteTreeEntryOwner(update))
}

/**
 * Moves a note in the tree to its new primary alias, e.g. after its title or path changed.
 *
 * @param rename The previous and the new primary alias of the note
 */
export const renameNoteTreeEntry = (rename: NoteTreeEntryRename): void => {
  store.dispatch(noteTreeActionsCreator.renameNoteTreeEntry(rename))
}

/**
 * Removes a note from the tree, e.g. after it was deleted.
 *
 * @param primaryAlias The primary alias of the note to remove
 */
export const removeNoteTreeEntry = (primaryAlias: string): void => {
  store.dispatch(noteTreeActionsCreator.removeNoteTreeEntry(primaryAlias))
}

/**
 * Clears the tree, e.g. after the user logged out.
 */
export const resetNoteTree = (): void => {
  store.dispatch(noteTreeActionsCreator.resetNoteTree())
}

/**
 * Expands or collapses the navigation sidebar and remembers the choice in the browser.
 *
 * @param open true to expand the sidebar
 */
export const setNoteTreeSidebarOpen = (open: boolean): void => {
  store.dispatch(noteTreeActionsCreator.setSidebarOpen(open))
  try {
    window.localStorage.setItem(SIDEBAR_OPEN_LOCAL_STORAGE_KEY, String(open))
  } catch (error) {
    logger.error('Saving to local storage failed', error)
  }
}

/**
 * Restores the expanded state of the navigation sidebar that was remembered in the browser.
 */
export const restoreNoteTreeSidebarOpen = (): void => {
  try {
    const storedValue = window.localStorage.getItem(SIDEBAR_OPEN_LOCAL_STORAGE_KEY)
    if (storedValue !== null) {
      store.dispatch(noteTreeActionsCreator.setSidebarOpen(storedValue === 'true'))
    }
  } catch (error) {
    logger.error('Reading from local storage failed', error)
  }
}
